import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Eye, Search, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const RHExpedientes = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Datos de ejemplo de expedientes
  const expedientes = [
    {
      id: 1,
      empleado: "Juan Pérez García",
      numeroExpediente: "EXP-2018-001",
      documentos: [
        { tipo: "Contrato", status: "Completo", fecha: "2018-03-15" },
        { tipo: "Identificación", status: "Completo", fecha: "2018-03-10" },
        { tipo: "Comprobante domicilio", status: "Completo", fecha: "2018-03-12" },
        { tipo: "RFC", status: "Completo", fecha: "2018-03-11" },
        { tipo: "CURP", status: "Completo", fecha: "2018-03-11" },
        { tipo: "NSS", status: "Completo", fecha: "2018-03-13" },
        { tipo: "Certificado médico", status: "Completo", fecha: "2023-01-15" }
      ],
      completitud: 100,
      ultimaActualizacion: "2023-01-15"
    },
    {
      id: 2,
      empleado: "María López Hernández",
      numeroExpediente: "EXP-2019-012",
      documentos: [
        { tipo: "Contrato", status: "Completo", fecha: "2019-07-20" },
        { tipo: "Identificación", status: "Completo", fecha: "2019-07-18" },
        { tipo: "Comprobante domicilio", status: "Pendiente", fecha: null },
        { tipo: "RFC", status: "Completo", fecha: "2019-07-19" },
        { tipo: "CURP", status: "Completo", fecha: "2019-07-19" },
        { tipo: "NSS", status: "Completo", fecha: "2019-07-20" },
        { tipo: "Certificado médico", status: "Por vencer", fecha: "2023-07-20" }
      ],
      completitud: 85,
      ultimaActualizacion: "2023-07-20"
    },
    {
      id: 3,
      empleado: "Carlos Ramírez Soto",
      numeroExpediente: "EXP-2020-025",
      documentos: [
        { tipo: "Contrato", status: "Completo", fecha: "2020-01-10" },
        { tipo: "Identificación", status: "Completo", fecha: "2020-01-08" },
        { tipo: "Comprobante domicilio", status: "Completo", fecha: "2020-01-09" },
        { tipo: "RFC", status: "Completo", fecha: "2020-01-09" },
        { tipo: "CURP", status: "Completo", fecha: "2020-01-09" },
        { tipo: "NSS", status: "Pendiente", fecha: null },
        { tipo: "Certificado médico", status: "Completo", fecha: "2024-01-10" }
      ],
      completitud: 85,
      ultimaActualizacion: "2024-01-10"
    },
    {
      id: 4,
      empleado: "Ana Martínez Torres",
      numeroExpediente: "EXP-2021-038",
      documentos: [
        { tipo: "Contrato", status: "Completo", fecha: "2021-05-12" },
        { tipo: "Identificación", status: "Completo", fecha: "2021-05-10" },
        { tipo: "Comprobante domicilio", status: "Completo", fecha: "2021-05-11" },
        { tipo: "RFC", status: "Completo", fecha: "2021-05-10" },
        { tipo: "CURP", status: "Completo", fecha: "2021-05-10" },
        { tipo: "NSS", status: "Completo", fecha: "2021-05-12" },
        { tipo: "Certificado médico", status: "Completo", fecha: "2024-05-12" }
      ],
      completitud: 100,
      ultimaActualizacion: "2024-05-12"
    }
  ];

  const filteredExpedientes = expedientes.filter(exp =>
    exp.empleado.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.numeroExpediente.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completo':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Pendiente':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'Por vencer':
        return <Clock className="w-4 h-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completo':
        return 'text-green-600';
      case 'Pendiente':
        return 'text-red-600';
      case 'Por vencer':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/recursos-humanos">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition"
              >
                <ArrowLeft className="w-6 h-6 text-gray-700" />
              </motion.button>
            </Link>
            <h1 className="text-4xl font-bold text-gray-800">📂 Expedientes de Personal</h1>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre o número de expediente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Resumen de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Total Expedientes</p>
            <p className="text-3xl font-bold text-purple-600">{expedientes.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Completos</p>
            <p className="text-3xl font-bold text-green-600">{expedientes.filter(e => e.completitud === 100).length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Incompletos</p>
            <p className="text-3xl font-bold text-orange-600">{expedientes.filter(e => e.completitud < 100).length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Documentos Pendientes</p>
            <p className="text-3xl font-bold text-red-600">
              {expedientes.reduce((total, exp) => 
                total + exp.documentos.filter(doc => doc.status === 'Pendiente').length, 0
              )}
            </p>
          </div>
        </div>

        {/* Lista de expedientes */}
        <div className="space-y-6">
          {filteredExpedientes.map((expediente, index) => (
            <motion.div
              key={expediente.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-1">{expediente.empleado}</h3>
                  <p className="text-gray-500 text-sm">{expediente.numeroExpediente}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${expediente.completitud === 100 ? 'bg-green-500' : 'bg-orange-500'}`}
                        style={{ width: `${expediente.completitud}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{expediente.completitud}%</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    Actualizado: {new Date(expediente.ultimaActualizacion).toLocaleDateString('es-MX')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {expediente.documentos.map((doc, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{doc.tipo}</p>
                        {doc.fecha && (
                          <p className="text-xs text-gray-500">
                            {new Date(doc.fecha).toLocaleDateString('es-MX')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(doc.status)}
                      <span className={`text-xs font-semibold ${getStatusColor(doc.status)}`}>
                        {doc.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 flex items-center justify-center gap-2 bg-purple-500 text-white py-2 rounded-lg hover:bg-purple-600 transition"
                >
                  <Eye className="w-4 h-4" />
                  Ver Expediente Completo
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
                >
                  <Download className="w-4 h-4" />
                  Descargar
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredExpedientes.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">No se encontraron expedientes con ese criterio de búsqueda.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RHExpedientes;
